import './style.css';
import { assetUrl, loadCatalog } from './assets';
import { initAds } from './ads';
import { RewardedAds } from './rewarded-ads';
import { initHomeVideo } from './homepage-video';
import { language, setLanguage, t, type Message } from './i18n';
import { buildShareUrl, shareGame } from './share';
import { RecordStore, STORAGE_KEY, validScore } from './storage';
import { initialLanguage } from './locale';
import { canonicalUrl, languagePath, localizedUrl, routeLanguage } from './seo.ts';
import { skinValue, valueLabel, type ThemeInfo } from './presentation';
import { availableThemes, showsSkinNumber } from './themes';
import { UnityGame, type UnityEvent, type LoadingStage, type GameMode } from './unity';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const canvas = $<HTMLCanvasElement>('game-canvas'), shell = $('game-shell');
const viewport = $('game-window');
let storage: Storage | null = null;
try { storage = window.localStorage; } catch { /* Restricted storage does not prevent playing. */ }
const store = new RecordStore(storage), query = new URLSearchParams(location.search);
const pageBasePath = new URL(document.baseURI).pathname;
setLanguage(initialLanguage(query.get('lang'), store.data.language, store.data.manualLanguage, navigator.languages?.[0], navigator.language,
  routeLanguage(location.pathname, pageBasePath)));
let catalog: ThemeInfo[] = [], theme = 'jelly', mode: GameMode = query.get('mode') === 'challenge' ? 'challenge' : 'casual', ready = false, busy = false;
let phase: UnityEvent['phase'] = 'home', score = 0, highest = 2, startedBest = 0;
let challengeState: UnityEvent = { type: 'challenge', phase: 'home', score: 0, best: 0, highest: 2, theme: 'jelly', combo: 0, counters: [] };
let spaceHeld = false, highestBallKey = '';
let displayedPower = -1;
let loadingProgress = 0, operation = 0, retryAction: () => Promise<void> = boot;
let loadingStage: LoadingStage = 'game';
const dialogs = [...document.querySelectorAll<HTMLDialogElement>('dialog')];
const game = new UnityGame(canvas, receive);
const rewardedAds = new RewardedAds((method, json) => game.send(method, json),
  () => phase === 'playing' && !dialogs.some(dialog => dialog.open));
const homeVideo = initHomeVideo($('home-preview'));
function syncHomeVideo() { homeVideo.setActive(phase === 'home' && ready && !busy && !dialogs.some(dialog => dialog.open)); }
const icons = {
  sound: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15 8a6 6 0 0 1 0 8M18 5a10 10 0 0 1 0 14"/></svg>',
  muted: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4zM16 9l5 6M21 9l-5 6"/></svg>',
  pause: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M8 5v14M16 5v14"/></svg>',
  fullscreen: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 3H3v6M15 3h6v6M21 15v6h-6M9 21H3v-6"/></svg>'
};
$('pause-btn').innerHTML = icons.pause; $('fullscreen-btn').innerHTML = icons.fullscreen;
const coarsePointer = matchMedia('(pointer: coarse)');
function setInputHint(touch: boolean) {
  const input = touch ? 'touch' : 'mouse';
  if (document.body.dataset.input !== input) document.body.dataset.input = input;
}
setInputHint(coarsePointer.matches);
coarsePointer.addEventListener('change', event => setInputHint(event.matches));
window.addEventListener('pointerdown', event => setInputHint(event.pointerType !== 'mouse'), { passive: true });
function updatePower(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) return;
  const percent = Math.round(Math.max(0, Math.min(100, value)));
  if (percent === displayedPower) return;
  displayedPower = percent;
  $('power-value').textContent = percent + '%';
  $('power-fill').style.transform = 'scaleX(' + percent / 100 + ')';
  $('power-meter').setAttribute('aria-valuenow', String(percent));
}
function updateControlLabels() {
  const fullscreenLabel = t(document.fullscreenElement ? 'exitFullscreen' : 'fullscreen');
  $('fullscreen-btn').setAttribute('aria-label', fullscreenLabel); $('fullscreen-btn').title = fullscreenLabel + ' (F)';
  $('pause-btn').setAttribute('aria-label', t('pauseAction')); $('pause-btn').title = t('pauseAction') + ' (Esc)';
}
document.addEventListener('fullscreenchange', () => { cancelSpaceCharge(); game.send('CancelPointer'); updateControlLabels(); });
function updateRecords() {
  $('challenge-help').hidden = mode !== 'challenge';
  const modeBest = mode === 'challenge' ? store.data.challengeBest : store.data.best;
  const bestLabel = modeBest.toLocaleString();
  $('home-best-value').textContent = bestLabel;
  $('home-record-title').textContent = t(mode === 'challenge' ? 'challengeBest' : 'best');
  $('home-stage-record').hidden = mode !== 'challenge'; $('home-stage-value').textContent = Math.max(store.data.challengeStage, validScore(challengeState.bestStage)).toLocaleString();
  for (const mirror of document.querySelectorAll<HTMLElement>('[data-home-best]')) mirror.textContent = bestLabel;
  $('storage-warning').hidden = store.available;
  $('screen-status').textContent = t('score') + ' ' + score + ', ' + t(mode === 'challenge' ? 'challengeBest' : 'best') + ' ' + modeBest;
  $('result-score').textContent = score.toLocaleString(); $('result-best').textContent = modeBest.toLocaleString();
  $('result-highest').textContent = valueLabel(highest);
  $('rail-best-value').textContent = bestLabel; $('rail-score-value').textContent = score.toLocaleString();
  for (const id of ['rail-best-value', 'rail-score-value']) $(id).style.setProperty('--digits', String($(id).textContent!.length));
  $('rail-highest-value').textContent = valueLabel(highest);
  updateHighestBall();
}
// Challenge controls, target picking and reward confirmation live in Unity.
function updateChallenge(message: UnityEvent) {
  challengeState = { ...challengeState, ...message, reward: message.reward ?? null };
  rewardedAds.update(challengeState);
  if (challengeState.counterSelecting || challengeState.counterCasting || challengeState.rewardOpen) cancelSpaceCharge();
}
function updateHighestBall() {
  const key = theme + ':' + highest;
  if (key === highestBallKey) return;
  highestBallKey = key;
  $<HTMLImageElement>('rail-highest-art').src = assetUrl('themes/' + theme + '/' + skinValue(highest) + '-thumb.webp');
  const number = $('rail-highest-number'); number.replaceChildren();
  number.hidden = !showsSkinNumber(theme);
  for (const digit of showsSkinNumber(theme) ? valueLabel(highest) : '') {
    const image = document.createElement('img'); image.src = assetUrl('digits/' + digit + '.webp'); image.alt = ''; number.append(image);
  }
  $('rail-highest-ball').style.setProperty('--number-width', theme === 'jelly' ? '75%' : '52.5%');
  $('rail-highest-ball').style.setProperty('--number-height', theme === 'jelly' ? '44%' : '30.8%');
}
function updateThemeLabels() {
  const info = catalog.find(item => item.id === theme);
  $('selected-theme').textContent = info ? language === 'en' ? info.en : info.label : '';
  $('rail-theme-name').textContent = $('selected-theme').textContent;
  for (const btn of document.querySelectorAll<HTMLElement>('.theme-option')) {
    const item = catalog.find(item => item.id === btn.dataset.theme)!;
    btn.querySelector('span')!.textContent = language === 'en' ? item.en : item.label;
    btn.setAttribute('aria-pressed', String(item.id === theme));
  }
}
function updateHomePreviews() {
  const holder = $('theme-thumbnails'); holder.replaceChildren();
  const info = catalog.find(item => item.id === theme);
  viewport.style.setProperty('--theme-background', info ? 'url(' + JSON.stringify(assetUrl(info.background)) + ')' : 'none');
  for (const value of [2, 4, 8, 16]) {
    const image = document.createElement('img'); image.src = assetUrl('themes/' + theme + '/' + value + '-thumb.webp'); image.alt = ''; holder.append(image);
  }
  updateThemeLabels();
  updateHighestBall();
}
function updateSound() {
  const btn = $('sound-btn'); btn.innerHTML = store.data.sound ? icons.sound : icons.muted;
  btn.setAttribute('aria-pressed', String(store.data.sound)); btn.setAttribute('aria-label', t(store.data.sound ? 'soundOn' : 'soundOff'));
}
function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta'); meta.setAttribute(attribute, key); document.head.append(meta);
  }
  meta.content = content;
}
function translatePage() {
  for (const element of document.querySelectorAll<HTMLElement>('[data-i18n]')) element.textContent = t(element.dataset.i18n as Message);
  for (const element of document.querySelectorAll<HTMLElement>('[data-copy-lang]')) {
    element.hidden = element.dataset.copyLang !== language;
    element.lang = element.dataset.copyLang === 'zh' ? 'zh-Hans' : 'en';
  }
  const pageUrl = localizedUrl(location.href, language, pageBasePath);
  if (pageUrl.href !== location.href) history.replaceState(history.state, '', pageUrl.href);
  query.delete('lang');
  const languageButton = $<HTMLAnchorElement>('language-btn');
  const otherLanguage = language === 'en' ? 'zh' : 'en';
  const alternateUrl = localizedUrl(location.href, otherLanguage, pageBasePath);
  // An English link opened in a new tab must override a saved Chinese preference.
  if (otherLanguage === 'en') alternateUrl.searchParams.set('lang', 'en');
  languageButton.href = alternateUrl.href;
  languageButton.hreflang = otherLanguage === 'zh' ? 'zh-Hans' : 'en';
  languageButton.lang = languageButton.hreflang;
  const languageLabel = document.createElement('span');
  languageLabel.textContent = language === 'en' ? '中文' : 'EN';
  languageLabel.lang = language === 'en' ? 'zh-Hans' : 'en';
  languageButton.replaceChildren(languageLabel);
  languageButton.setAttribute('aria-label', t(language === 'en' ? 'switchToChinese' : 'switchToEnglish'));
  const homeLink = document.querySelector<HTMLAnchorElement>('.header-wordmark');
  homeLink?.setAttribute('aria-label', t('homeLink'));
  if (homeLink) homeLink.href = languagePath(language, pageBasePath);
  document.querySelector<HTMLElement>('.header-actions')?.setAttribute('aria-label', t('pageControls'));
  document.querySelector<HTMLElement>('.game-column')?.setAttribute('aria-label', t('gameWindow'));
  updateControlLabels(); $('help-btn').setAttribute('aria-label', t('help'));
  canvas.setAttribute('aria-label', t('gameHint') + '. ' + t('touchHint') + '. ' + t('keyboard'));
  for (const btn of document.querySelectorAll<HTMLElement>('.dialog-close')) btn.setAttribute('aria-label', t('close'));
  document.title = t('metaTitle');
  setMeta('name', 'description', t('metaDescription'));
  setMeta('property', 'og:title', t('metaTitle'));
  setMeta('property', 'og:description', t('metaDescription'));
  setMeta('property', 'og:site_name', t('brand'));
  setMeta('property', 'og:locale', language === 'zh' ? 'zh_CN' : 'en_US');
  setMeta('property', 'og:locale:alternate', language === 'zh' ? 'en_US' : 'zh_CN');
  setMeta('name', 'twitter:title', t('metaTitle'));
  setMeta('name', 'twitter:description', t('metaDescription'));
  setMeta('property', 'og:image:alt', t('title'));
  const site = import.meta.env.VITE_SITE_URL;
  const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (site && canonical) {
    canonical.href = canonicalUrl(site, language);
    setMeta('property', 'og:url', canonical.href);
  }
  const schemaElement = document.getElementById('game-schema');
  if (schemaElement) {
    const schema = JSON.parse(schemaElement.textContent ?? '{}');
    schema.name = t('brand'); schema.description = t('metaDescription'); schema.inLanguage = language === 'zh' ? 'zh-Hans' : 'en';
    if (canonical) schema.url = canonical.href;
    schemaElement.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
  }
  $('privacy-btn').textContent = t('privacy'); $('privacy-btn').setAttribute('aria-label', t('privacy'));
  $('privacy-title').textContent = t('privacy');
  document.querySelector<HTMLLabelElement>('label[for="share-url"]')?.replaceChildren(document.createTextNode(t('inviteLink')));
  updateSound(); updateThemeLabels(); updateRecords();
  homeVideo.translate();
  const target = validScore(Number(query.get('target'))); $('friend-target').hidden = !target;
  if (target) $('friend-target').textContent = t('challenge') + target.toLocaleString() + (language === 'zh' ? ' 分' : '');
  if (phase === 'loading') $('loading-text').textContent = t(loadingStage === 'theme' ? 'loadingTheme' : 'loading') + ' ' + Math.round(loadingProgress * 100) + '%';
}
function showDialog(id: string) {
  const dialog = $<HTMLDialogElement>(id);
  if (!dialog.open) {
    // A modal must belong to the fullscreen subtree to stay visible there.
    const parent = document.fullscreenElement === shell ? shell : document.body;
    if (dialog.parentElement !== parent) parent.append(dialog);
    dialog.showModal();
    syncHomeVideo();
  }
}
function closeDialogs() { for (const dialog of dialogs) if (dialog.open) dialog.close(); }
function change(next: UnityEvent['phase']) {
  if (next !== 'playing') cancelSpaceCharge();
  phase = next; document.body.dataset.screen = next; shell.dataset.screen = phase; shell.setAttribute('aria-busy', String(busy || phase === 'loading'));
  $('home-screen').hidden = phase !== 'home' || !ready; canvas.hidden = phase === 'home';
  // Keep the square canvas measurable while Unity starts; show it when ready.
  canvas.style.visibility = phase === 'loading' ? 'hidden' : '';
  $('play-controls').hidden = !['playing', 'paused', 'over'].includes(phase);
  $('loading-screen').hidden = phase !== 'loading'; $('game-hint').hidden = phase === 'home' || phase === 'loading';
  $<HTMLButtonElement>('start-btn').disabled = !ready || busy;
  if (phase === 'home' || (phase === 'playing' && !challengeState.rewardOpen)) closeDialogs();
  if (phase === 'paused') showDialog('pause-dialog');
  if (phase === 'over') {
    $<HTMLDialogElement>('pause-dialog').close(); $('new-best-label').hidden = score <= startedBest; showDialog('result-dialog');
  }
  updateRecords();
  syncHomeVideo();
}
function receive(message: UnityEvent) {
  if (message.type === 'ready') return;
  if (message.type === 'reward-video-request') { rewardedAds.request(message.videoToken); return; }
  if (message.mode) mode = message.mode;
  const stageHigh = Math.max(
    validScore(challengeState.bestStage),
    validScore(message.bestStage),
    validScore(message.stage),
  );
  const stageChanged = stageHigh > validScore(challengeState.bestStage);
  if (stageChanged) challengeState.bestStage = stageHigh;
  if (stageHigh > store.data.challengeStage) store.save({ challengeStage: stageHigh });
  if (stageChanged) updateRecords();
  if (message.type === 'challenge') {
    updateChallenge(message);
    challengeState.bestStage = Math.max(stageHigh, validScore(challengeState.bestStage));
    updateRecords();
    return;
  }
  if (phase === 'home') return;
  updatePower(message.power);
  if (message.type === 'power') return;
  score = validScore(message.score); highest = Math.max(2, validScore(message.highest));
  if (message.type === 'score' || (message.type === 'phase' && message.phase === 'over')) {
    if (mode === 'challenge') store.save({ challengeBest: score, challengeStage: Math.max(store.data.challengeStage, validScore(challengeState.bestStage)) });
    else store.save({ best: score });
    updateRecords();
  }
  if (message.type === 'phase' && message.phase !== 'home') change(message.phase);
}
let toastTimer: ReturnType<typeof setTimeout> | undefined;
function toast(text: string) {
  (document.fullscreenElement === shell ? shell : document.body).append($('toast'));
  clearTimeout(toastTimer); $('toast').textContent = text; $('toast').hidden = false;
  toastTimer = setTimeout(() => { $('toast').hidden = true; }, 3000);
}
async function start() {
  if (!ready || busy) return;
  if (phase === 'over' && game.loaded) {
    startedBest = mode === 'challenge' ? store.data.challengeBest : store.data.best; closeDialogs(); game.send('Replay'); change('playing'); canvas.focus({ preventScroll: true }); return;
  }
  const token = ++operation; busy = true; startedBest = mode === 'challenge' ? store.data.challengeBest : store.data.best; score = 0; highest = 2; loadingProgress = 0;
  updatePower(50);
  loadingStage = 'game';
  closeDialogs(); $('loading-screen').classList.remove('error'); $('retry-btn').hidden = true; retryAction = start;
  change('loading'); $('loading-text').textContent = t('loading') + ' 0%'; $('loading-home-btn').hidden = true;
  shell.scrollIntoView({ behavior: 'instant', block: 'start' });
  try {
    await game.load({ theme, language, sound: store.data.sound, mode, best: Math.min(2147483647, mode === 'challenge' ? store.data.challengeBest : store.data.best), bestStage: store.data.challengeStage, reduceMotion: matchMedia('(prefers-reduced-motion: reduce)').matches }, (progress, step) => {
      if (token !== operation) return;
      loadingProgress = progress; loadingStage = step;
      $('loading-text').textContent = t(step === 'theme' ? 'loadingTheme' : 'loading') + ' ' + Math.round(progress * 100) + '%';
    });
    if (token !== operation) return;
    busy = false; change('playing'); canvas.focus({ preventScroll: true });
    if (document.hidden || !document.hasFocus()) pause();
  } catch (error) {
    if (token !== operation) return;
    console.error(error); await game.quit(); busy = false;
    $('loading-screen').classList.add('error'); $('loading-text').textContent = t('error'); $('retry-btn').hidden = false;
    $<HTMLButtonElement>('loading-home-btn').hidden = false;
  }
}
async function goHome() {
  ++operation; busy = true; game.send('ReturnHome'); change('home'); updateHomePreviews();
  try { await game.quit(); } finally { busy = false; change('home'); }
}
function pause() { if (phase === 'playing') { game.send('Pause'); change('paused'); } }
function resume() { if (phase === 'paused') { game.send('Resume'); change('playing'); canvas.focus({ preventScroll: true }); } }
function selectTheme(id: string) {
  if (phase !== 'home' || busy || !catalog.some(item => item.id === id)) return;
  theme = id; store.save({ theme }); updateHomePreviews(); $<HTMLDialogElement>('theme-dialog').close();
}
function selectMode(next: GameMode) {
  if (phase !== 'home' || busy) return;
  mode = next; $('casual-mode-btn').setAttribute('aria-pressed', String(mode === 'casual')); $('challenge-mode-btn').setAttribute('aria-pressed', String(mode === 'challenge'));
  updateRecords();
}
for (const [id, next] of [['casual-mode-btn', 'casual'], ['challenge-mode-btn', 'challenge']] as const) $(id).addEventListener('click', () => selectMode(next));
for (const id of ['start-btn', 'replay-btn']) $(id).addEventListener('click', () => void start());
$('pause-btn').addEventListener('click', pause); $('resume-btn').addEventListener('click', resume);
for (const id of ['pause-home-btn', 'result-home-btn', 'loading-home-btn']) $(id).addEventListener('click', () => void goHome());
$('theme-btn').addEventListener('click', () => { if (phase === 'home' && !busy) showDialog('theme-dialog'); });
$<HTMLAnchorElement>('language-btn').addEventListener('click', event => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  const nextLanguage = language === 'en' ? 'zh' : 'en';
  setLanguage(nextLanguage); store.save({ language: nextLanguage, manualLanguage: true });
  translatePage(); game.send('SetLanguage', language);
  game.configure({ theme, language, sound: store.data.sound, mode, best: Math.min(2147483647, mode === 'challenge' ? store.data.challengeBest : store.data.best), bestStage: store.data.challengeStage, reduceMotion: matchMedia('(prefers-reduced-motion: reduce)').matches });
});
$('sound-btn').addEventListener('click', () => {
  store.save({ sound: !store.data.sound }); updateSound(); game.send('SetSound', store.data.sound ? '1' : '0');
  game.configure({ theme, language, sound: store.data.sound, mode, best: Math.min(2147483647, mode === 'challenge' ? store.data.challengeBest : store.data.best), bestStage: store.data.challengeStage, reduceMotion: matchMedia('(prefers-reduced-motion: reduce)').matches });
});
function utilityDialog(id: string) { if (phase === 'playing') { pause(); $<HTMLDialogElement>('pause-dialog').close(); } showDialog(id); }
$('help-btn').addEventListener('click', () => utilityDialog('help-dialog'));
$('privacy-btn').addEventListener('click', () => utilityDialog('privacy-dialog'));
for (const dialog of dialogs) {
  dialog.querySelector('.dialog-close')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => {
    if (event.target !== dialog || ['pause-dialog', 'result-dialog'].includes(dialog.id)) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
  dialog.addEventListener('cancel', event => {
    if (dialog.id === 'pause-dialog') { event.preventDefault(); dialog.close(); resume(); }
    else if (dialog.id === 'result-dialog') { event.preventDefault(); void goHome(); }
  });
  dialog.addEventListener('close', () => {
    if (['help-dialog', 'privacy-dialog'].includes(dialog.id) && phase === 'paused') showDialog('pause-dialog');
    syncHomeVideo();
  });
}
async function invite() {
  const url = buildShareUrl(location.href, theme, phase === 'over' ? score : mode === 'challenge' ? store.data.challengeBest : store.data.best, language, mode);
  const result = await shareGame(url);
  if (result === 'copied') toast(t('shared'));
  if (result === 'manual') { $<HTMLInputElement>('share-url').value = url; showDialog('share-dialog'); $<HTMLInputElement>('share-url').select(); }
}
for (const id of ['invite-btn', 'result-invite-btn']) $(id).addEventListener('click', () => void invite());
$('copy-btn').addEventListener('click', async () => {
  const input = $<HTMLInputElement>('share-url'); input.select();
  try { await navigator.clipboard.writeText(input.value); $<HTMLDialogElement>('share-dialog').close(); toast(t('shared')); }
  catch { input.focus(); input.select(); }
});
async function fullscreen() {
  try {
    if (document.fullscreenElement) await document.exitFullscreen(); else await shell.requestFullscreen();
    if (phase === 'playing') canvas.focus({ preventScroll: true });
  }
  catch { toast(language === 'zh' ? '当前浏览器不支持全屏。' : 'Fullscreen is unavailable.'); }
}
$('fullscreen-btn').addEventListener('click', () => void fullscreen());
function interactiveTarget(target: EventTarget | null) { return target instanceof Element && !!target.closest('input,textarea,button,a,summary'); }
function cancelSpaceCharge() {
  if (!spaceHeld) return;
  spaceHeld = false; game.send('Keyboard', 'charge-cancel');
}
window.addEventListener('keydown', event => {
  if (event.isComposing || dialogs.some(dialog => dialog.open) || interactiveTarget(event.target)) return;
  if (challengeState.counterSelecting || challengeState.counterCasting || challengeState.rewardOpen) {
    if (event.key === 'Escape') { event.preventDefault(); pause(); }
    else if (event.key.toLowerCase() !== 'f') event.preventDefault();
    if (event.key.toLowerCase() === 'f') { event.preventDefault(); void fullscreen(); }
    return;
  }
  if (event.code === 'Space' || event.key === ' ') {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    setInputHint(false);
    if (phase === 'home' && !event.repeat) { event.preventDefault(); void start(); }
    if (phase === 'playing') {
      event.preventDefault();
      if (!spaceHeld && !event.repeat) { spaceHeld = true; game.send('Keyboard', 'charge-start'); }
    }
    return;
  }
  const actions: Record<string, string> = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
  if (event.key === 'Escape') { event.preventDefault(); pause(); return; }
  if (event.key.toLowerCase() === 'f') { event.preventDefault(); void fullscreen(); return; }
  if (phase === 'playing' && actions[event.key]) {
    setInputHint(false);
    event.preventDefault(); game.send('Keyboard', actions[event.key]);
  }
});
window.addEventListener('keyup', event => {
  if (!(event.code === 'Space' || event.key === ' ') || !spaceHeld) return;
  event.preventDefault(); spaceHeld = false;
  const fire = phase === 'playing' && !event.isComposing && !dialogs.some(dialog => dialog.open) && !interactiveTarget(event.target);
  game.send('Keyboard', fire ? 'charge-release' : 'charge-cancel');
});
document.addEventListener('focusin', event => { if (interactiveTarget(event.target)) cancelSpaceCharge(); });
window.addEventListener('blur', pause); window.addEventListener('orientationchange', pause);
function sendMousePosition(event: PointerEvent, action: 'down' | 'move' | 'drag' | 'up') {
  if (phase !== 'playing' || dialogs.some(dialog => dialog.open)) return;
  setInputHint(false);
  const bounds = canvas.getBoundingClientRect();
  if (bounds.width <= 0 || bounds.height <= 0) return;
  const x = (event.clientX - bounds.left) / bounds.width, y = 1 - (event.clientY - bounds.top) / bounds.height;
  if (event.pointerType === 'mouse') game.send('AimMouse', JSON.stringify({ action, x, y }));
}
canvas.addEventListener('pointerdown', event => {
  if (event.button !== 0) return;
  if (event.pointerType !== 'mouse') return;
  cancelSpaceCharge(); canvas.focus({ preventScroll: true }); sendMousePosition(event, 'down');
});
canvas.addEventListener('pointermove', event => { if (event.pointerType === 'mouse' && (event.buttons === 0 || (event.buttons & 1) !== 0)) sendMousePosition(event, (event.buttons & 1) !== 0 ? 'drag' : 'move'); });
window.addEventListener('pointerup', event => {
  if (event.button !== 0) return;
  if (event.pointerType === 'mouse') sendMousePosition(event, 'up');
});
canvas.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') game.send('CancelPointer'); });
canvas.addEventListener('pointercancel', () => { game.send('CancelPointer'); });
document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); });
window.addEventListener('storage', event => {
  if (event.key === STORAGE_KEY) { store.mergeExternal(event.newValue); updateRecords(); game.send('SetBest', String(Math.min(2147483647, mode === 'challenge' ? store.data.challengeBest : store.data.best))); }
});
async function boot() {
  busy = true; change('home'); $('loading-screen').hidden = false; $('retry-btn').hidden = true; $('loading-text').textContent = t('ready');
  try {
    catalog = availableThemes((await loadCatalog()).themes);
    const wanted = query.get('theme') ?? store.data.theme; theme = catalog.some(item => item.id === wanted) ? wanted : 'jelly';
    store.save({ theme });
    const grid = $('theme-grid'); grid.replaceChildren();
    for (const info of catalog) {
      const btn = document.createElement('button'); btn.className = 'theme-option'; btn.dataset.theme = info.id;
      const image = document.createElement('img'); image.src = assetUrl('themes/' + info.id + '/16-thumb.webp'); image.alt = ''; image.loading = 'lazy';
      const label = document.createElement('span'); btn.append(image, label); btn.addEventListener('click', () => selectTheme(info.id)); grid.append(btn);
    }
    ready = true; busy = false; updateHomePreviews(); translatePage(); change('home'); selectMode(mode);
  } catch (error) {
    console.error(error); busy = false; retryAction = boot;
    shell.setAttribute('aria-busy', 'false');
    $('loading-screen').classList.add('error'); $('loading-text').textContent = t('error'); $('retry-btn').hidden = false;
  }
}
$('retry-btn').addEventListener('click', () => void retryAction());
translatePage(); initAds(); void boot();
